import React from 'react';
import { format } from 'date-fns';
import {
    DataTable,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CardHeading,
    PlusButton,
} from '@ucc/react/ui';
import { withRenderers } from '~/utils';
import { useDeleteWithConfirmation } from '~/hooks';
import { usePxConnectSchadenbericht } from '~/pages/projects/hooks/usePxConnectSchadenbericht';
import { PX_BEARBEITUNGSSTAND_LABEL, type PxBearbeitungsstand } from '~/meta/data/px-connect';

type Props = {
    projectId?: number;
    serviceRequestId?: string;
    /** Öffnet das PX-Connect-Schadenbericht-Modal (Anlegen wie Bearbeiten). */
    onOpenPxConnect: () => void;
    /** `pxconnect.schadenbericht/create` — schaltet „Neuer"/„Bearbeiten" frei. */
    canCreate?: boolean;
    /** `pxconnect.schadenbericht/delete` — schaltet „Löschen" frei. */
    canDelete?: boolean;
};

const formatDate = (iso?: string) => (iso ? format(new Date(iso), 'dd.MM.yyyy') : '');

/**
 * PX-Connect-Schadenbericht-Block auf der Kalkulationsseite (ADR 0006).
 *
 * Listet den einen gespeicherten Bericht des Schadens (Projekt) mit Schadenursache,
 * angelegt am/durch und versendet am. „Neuer" ist deaktiviert, sobald ein Bericht
 * existiert; „Löschen" ist deaktiviert, sobald der Bericht versendet wurde.
 */
export const PxConnectSchadenberichtBlock = ({
    projectId,
    serviceRequestId,
    onOpenPxConnect,
    canCreate = false,
    canDelete = false,
}: Props) => {
    const { draft, exists, letzterStand, loading, remove } = usePxConnectSchadenbericht({
        projectId,
        serviceRequestId,
    });

    const { deleteWithConfirmation } = useDeleteWithConfirmation({
        deleteMutation: async () => {
            await remove();
            // useDeleteWithConfirmation erwartet die {data:{item:{item,error}}}-Form.
            return { data: { item: { item: {}, error: null } } };
        },
        dependencies: [remove],
    });

    const isSent = Boolean(draft?.sentAt);

    const data = React.useMemo(
        () =>
            exists && draft
                ? [
                      {
                          id: 'px-schadenbericht',
                          schadenursache: draft.schadenursache || '',
                          createdAt: draft.createdAt,
                          createdByName: draft.createdByName || '',
                          sentAt: draft.sentAt,
                          bearbeitungsstand: letzterStand,
                      },
                  ]
                : [],
        [exists, draft, letzterStand]
    );

    const controls = React.useMemo(
        () => [
            {
                // Ohne `create`-Recht bleibt der Bericht ansehbar (Tabelle), aber der
                // Bearbeiten-Einstieg wird ausgeblendet ("ansehen darf jeder").
                title: 'Bearbeiten',
                props: { onClick: () => onOpenPxConnect() },
                enabled: (): boolean => canCreate,
            },
            { title: 'divider', enabled: () => canCreate && canDelete },
            {
                title: 'Löschen',
                props: {
                    color: 'red.400',
                    // Versendete Berichte lassen sich nicht mehr löschen (ADR 0006).
                    isDisabled: isSent,
                    onClick: () => {
                        if (isSent) return;
                        deleteWithConfirmation();
                    },
                },
                // Löschen ist an `pxconnect.schadenbericht/delete` gebunden (Spec).
                enabled: (): boolean => canDelete,
            },
        ],
        [isSent, onOpenPxConnect, deleteWithConfirmation, canCreate, canDelete]
    );

    const columns = React.useMemo(
        () =>
            withRenderers(
                [
                    {
                        header: 'Schadenursache',
                        accessor: 'schadenursache',
                        cell: (info: any) => info.getValue() || '—',
                    },
                    {
                        header: 'angelegt am',
                        accessor: 'createdAt',
                        cell: (info: any) => formatDate(info.getValue()),
                    },
                    {
                        header: 'angelegt durch',
                        accessor: 'createdByName',
                        cell: (info: any) => info.getValue() || '',
                    },
                    {
                        header: 'versendet am',
                        accessor: 'sentAt',
                        cell: (info: any) =>
                            info.getValue() ? formatDate(info.getValue()) : 'noch nicht versendet',
                    },
                    {
                        // Stand beim letzten Versand (UCPWO-410); vor dem ersten Versand leer.
                        header: 'Bearbeitungsstand',
                        accessor: 'bearbeitungsstand',
                        cell: (info: any) => {
                            const stand = info.getValue() as PxBearbeitungsstand | undefined;
                            return stand ? PX_BEARBEITUNGSSTAND_LABEL[stand] : '—';
                        },
                    },
                    { accessor: 'controls', width: 20, renderer: { name: 'Controls' } },
                ],
                controls
            ),
        [controls]
    );

    return (
        <Card boxShadow="none" data-test-id="px-connect-schadenbericht-card">
            <CardHeader>
                <CardHeading>PX Connect Schadenbericht</CardHeading>
                <CardActions>
                    {canCreate && (
                        <PlusButton
                            isDisabled={loading || exists}
                            data-test-id="button-new-px-connect-schadenbericht"
                            onClick={onOpenPxConnect}
                        >
                            Neuer PX Connect Schadenbericht
                        </PlusButton>
                    )}
                </CardActions>
            </CardHeader>
            <CardContent>
                <DataTable columns={columns} data={data} loading={loading} />
            </CardContent>
        </Card>
    );
};
